package com.banyan.flightviewer.web.components

import com.banyan.flightviewer.data.AirportDatabase
import com.banyan.flightviewer.web.components.airport.AirportCache
import com.banyan.flightviewer.web.components.airport.mountAirportGeneralTab
import com.banyan.flightviewer.web.components.airport.mountAirportOnGroundTab
import com.banyan.flightviewer.web.components.airport.mountFlightListTab
import com.banyan.flightviewer.web.dom.bindFlow
import com.banyan.flightviewer.web.dom.button
import com.banyan.flightviewer.web.dom.div
import com.banyan.flightviewer.web.dom.onClick
import com.banyan.flightviewer.web.dom.removeAllChildren
import com.banyan.flightviewer.web.state.WebAppContainer
import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import org.w3c.dom.Element

/**
 * Airport info panel: header + tab bar + active tab content.
 *
 * Tabs (mirrors desktop [AirportInfoPanel.kt](desktop/src/main/kotlin/com/banyan/flightviewer/ui/AirportInfoPanel.kt)):
 *  - General  -- photo, airport info (city/elev/tz/coords), weather, ATC feeds
 *  - Departures  -- FR24 scheduled/live departures (cached 5min via AirportCache)
 *  - Arrivals    -- FR24 scheduled/live arrivals (same caching)
 *  - On Ground   -- live aircraft on tarmac within 3km of the airport
 *
 * Active tab index is held in a local [MutableStateFlow] captured by this
 * mount so tab state survives renders within a single airport selection;
 * a new airport selection rebuilds from scratch (General).
 */
fun mountAirportPanel(parent: Element, app: WebAppContainer) {
    val container = parent.div(className = "fv-airport")
    container.asDynamic().style.display = "none"

    val activeTab = MutableStateFlow(Tab.GENERAL)

    // `render(code)` is the full panel rebuild for a new airport.
    // `renderTabContent(code)` swaps only the bottom half when the user
    // clicks a tab -- header/feeds stay.
    fun render(code: String?) {
        container.removeAllChildren()
        if (code == null) {
            container.asDynamic().style.display = "none"
            return
        }
        container.asDynamic().style.display = "block"
        activeTab.value = Tab.GENERAL     // reset on new airport

        // ── Header ─────────────────────────────────────────────────────
        val header = container.div(className = "fv-airport__header")
        val h3 = document.createElement("h3")
        h3.textContent = code.uppercase()
        header.appendChild(h3)
        header.div(className = "fv-airport__spacer") { }

        val name = AirportDatabase.getName(code)

        // Recenter-on-airport button (↗). Looks up the airport's coords
        // in AirportDatabase and fires a map-focus request; panMapTo
        // handles the mobile bottom-sheet offset so the airport marker
        // lands above the sheet rather than hidden under it.
        val recenterBtn = header.button(className = "fv-detail__icon-btn") {
            textContent = "\u2197"
            title = "Recenter map on this airport"
        }
        recenterBtn.onClick {
            val apt = AirportDatabase.getAirportsWithLocations()
                .firstOrNull { it.code.equals(code, ignoreCase = true) }
            if (apt != null) app.uiState.requestMapFocus(apt.lat, apt.lon)
        }

        // Share button: mirrors DetailPanel's flight share button. Builds a
        // `/airport/<code>` URL and hands off to the shared [shareUrl]
        // helper (Web Share API → clipboard → legacy copy). Uses the
        // iOS-style share glyph (box + up-arrow) inline via SVG.
        val shareBtn = header.button(className = "fv-detail__icon-btn") {
            title = "Share this airport"
        }
        shareBtn.innerHTML = SHARE_ICON_SVG
        shareBtn.onClick {
            val host = kotlinx.browser.window.location.origin
            val shareLink = "$host/airport/${code.uppercase()}"
            val titleText = if (name != code) "$code ($name) on FlightView" else "$code on FlightView"
            shareUrl(
                app = app,
                url = shareLink,
                title = titleText,
                text = "Live traffic at $code on FlightView",
            )
        }

        // Favorite-airport star. Mirrors DetailPanel's flight-bookmark star:
        // filled amber when favorited, outline when not. Tap toggles and
        // persists to localStorage through SettingsManager.
        val favBtn = header.button(className = "fv-detail__icon-btn") {
            title = "Favorite airport"
        }
        fun renderFavBtn(isFav: Boolean) {
            favBtn.textContent = if (isFav) "\u2605" else "\u2606"
            favBtn.className =
                if (isFav) "fv-detail__icon-btn active" else "fv-detail__icon-btn"
        }
        renderFavBtn(app.appState.isAirportFavorite(code))
        favBtn.onClick {
            val nowFav = !app.appState.isAirportFavorite(code)
            if (nowFav) {
                app.appState.addAirportFavorite(
                    com.banyan.flightviewer.state.AirportFavorite(
                        code = code.uppercase(),
                        name = if (name != code) name else "",
                    ),
                )
            } else {
                app.appState.removeAirportFavorite(code)
            }
            app.settingsManager.saveAirportFavorites(app.appState.airportFavorites.value)
            renderFavBtn(nowFav)
        }
        // React to external changes (e.g. removal via the favorites panel)
        // so the star stays in sync while this airport is open.
        app.appState.scope.bindFlow(app.appState.airportFavorites) {
            renderFavBtn(app.appState.isAirportFavorite(code))
        }

        header.button(className = "fv-detail__icon-btn") {
            textContent = "\u2715"
            title = "Close"
            onClick { app.appState.selectAirport(null) }
        }

        if (name != code) {
            container.div(className = "fv-airport__sub") { textContent = name }
        }

        // ── Tab bar ────────────────────────────────────────────────────
        val tabBar = container.div(className = "fv-airport__tabs")
        val tabButtons = mutableMapOf<Tab, Element>()
        Tab.values().forEach { tab ->
            val btn = tabBar.button(className = "fv-airport__tab") {
                textContent = tab.label
            }
            btn.onClick { activeTab.value = tab }
            tabButtons[tab] = btn
        }

        // ── Active tab content ─────────────────────────────────────────
        val tabContent = container.div(className = "fv-airport__tab-content")

        fun renderTab(tab: Tab) {
            tabContent.removeAllChildren()
            when (tab) {
                Tab.GENERAL -> mountAirportGeneralTab(tabContent, app, code)
                Tab.DEPARTURES -> mountFlightListTab(tabContent, app.appState.scope, isDeparture = true) {
                    val cached = AirportCache.getDepartures(code)
                    if (cached != null) cached
                    else app.serverClient.getAirportDepartures(code).also { AirportCache.putDepartures(code, it) }
                }
                Tab.ARRIVALS -> mountFlightListTab(tabContent, app.appState.scope, isDeparture = false) {
                    val cached = AirportCache.getArrivals(code)
                    if (cached != null) cached
                    else app.serverClient.getAirportArrivals(code).also { AirportCache.putArrivals(code, it) }
                }
                Tab.ON_GROUND -> mountAirportOnGroundTab(tabContent, app, code)
            }
        }

        // Wire tab-bar active class + content swap to the activeTab flow.
        // Initial render handled by the flow's current value.
        app.appState.scope.bindFlow(activeTab) { tab ->
            tabButtons.forEach { (t, btn) ->
                btn.className = if (t == tab) "fv-airport__tab active" else "fv-airport__tab"
            }
            renderTab(tab)
        }
    }

    app.appState.scope.bindFlow(app.appState.selectedAirport) { code ->
        render(code)
    }
}

private enum class Tab(val label: String) {
    GENERAL("General"),
    DEPARTURES("Departures"),
    ARRIVALS("Arrivals"),
    ON_GROUND("On Ground"),
}
